import { useState } from "react";
import { ExternalLink, Mail, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo, buildFaqJsonLd } from "@/components/Seo";
import { FadeUp } from "@/components/anim";
import { BOOKING_URL, CONTACT, TERM } from "@/lib/site";

const FAQS = [
  {
    q: "When do classes run? What are the term dates?",
    a: `Classes run term-time. The autumn term starts on ${TERM.start} and runs to ${TERM.end}, with a two-week half term break from ${TERM.halfTerm} (no classes). You can book any time — your place is ready and waiting for the start of term.`,
  },
  {
    q: "Does my child need any experience?",
    a: "None at all. Most of our dancers started as complete beginners — including the ones now taking Intermediate exams. Just turn up in comfy clothes and we'll sort the rest.",
  },
  {
    q: "What should they wear for a first class?",
    a: "Anything they can move in — leggings and a t-shirt is perfect. Bare feet or plimsolls are fine to start. Once they're hooked (they will be), we'll point you to the right shoes for their style.",
  },
  {
    q: "Do parents stay during class?",
    a: "For Happy Feet and Toddler classes, absolutely — you're part of the fun while little ones settle. For older groups, most parents drop off and grab a coffee. Both venues have somewhere to wait.",
  },
  {
    q: "My child has additional needs — will they be looked after?",
    a: "Yes — properly. We teach plenty of SEND and neurodiverse children, and we adapt pace, structure and sensory load to suit each dancer. Tell Kerry what helps your child feel comfortable and that's exactly what we'll do.",
  },
  {
    q: "Are exams compulsory?",
    a: "No. ISTD exams are there for dancers who want them (and our record speaks for itself), but plenty of children dance with us purely for the joy of it. Both are exactly right.",
  },
  {
    q: "How do payments work?",
    a: "Everything runs through ClassForKids — secure card payments, easy rebooking each term, and your booking history all in one place. No cash in envelopes, we promise.",
  },
];

export default function Book() {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      <Seo
        title="Book a Class"
        description={"Book dance classes at Kerry's, Whitstable online — secure booking powered by ClassForKids. Ballet, tap, modern, musical theatre, street dance and more for ages 2–18."}
        path="/book"
        jsonLd={buildFaqJsonLd(FAQS)}
      />
      <section className="grain border-b-[0.5px] border-foreground/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24">
          <FadeUp className="max-w-3xl">
            <Badge variant="outline" className="rounded-none border-[0.5px] border-foreground bg-card font-bold uppercase tracking-[0.15em] text-[11px] px-4 py-1.5 mb-6">
              Secure booking &middot; powered by ClassForKids
            </Badge>
            <h1 className="font-display uppercase text-6xl sm:text-8xl lg:text-[6.5rem] leading-[0.88]">
              Grab a spot
            </h1>
            <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Browse the schedule, pick a class, book in about two minutes. First-timers always welcome &mdash; no
              experience needed. Book any time &mdash; classes run term-time, starting with the new term on {TERM.start}.
            </p>
            <div className="mt-8 inline-flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun-pale))] px-5 py-3.5" data-testid="book-term-dates-banner">
              <p className="text-sm font-bold uppercase tracking-[0.12em]">{TERM.line}</p>
              <p className="text-sm text-foreground/70">{TERM.halfTermLine}</p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <FadeUp>
          <div className="border-[0.5px] border-foreground bg-card relative">
            {!loaded && (
              <div className="absolute inset-0 p-6 space-y-4 bg-card z-10" data-testid="booking-iframe-loading">
                <Skeleton className="h-10 w-1/2 rounded-none" />
                <Skeleton className="h-24 w-full rounded-none" />
                <Skeleton className="h-24 w-full rounded-none" />
                <Skeleton className="h-24 w-full rounded-none" />
                <p className="text-sm text-muted-foreground text-center pt-4">Loading the booking system&hellip;</p>
              </div>
            )}
            <iframe
              src={BOOKING_URL}
              title="Kerry's class booking, powered by ClassForKids"
              className="w-full h-[1100px] border-0"
              onLoad={() => setLoaded(true)}
              data-testid="booking-classforkids-iframe"
            />
          </div>
        </FadeUp>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6">
          <FadeUp delay={0.05} className="md:col-span-7">
            <div className="border-[0.5px] border-foreground bg-card p-8 sm:p-10 h-full" data-testid="booking-iframe-fallback">
              <h2 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95]">Booking window playing up?</h2>
              <p className="text-muted-foreground mt-3 leading-relaxed max-w-md">
                Some networks block embedded booking pages. No drama &mdash; the same system opens perfectly in its own tab.
              </p>
              <Button
                asChild
                className="rounded-none mt-6 bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-xs h-11 px-6"
                data-testid="booking-open-new-tab-button"
              >
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  Open booking site <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-5 md:mt-10">
            <div className="border-[0.5px] border-foreground bg-secondary grain p-8 sm:p-10 h-full">
              <h2 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95]">Rather have a chat first?</h2>
              <p className="text-muted-foreground mt-3 leading-relaxed">
                Not sure which class fits? Kerry will happily point you to the right one.
              </p>
              <div className="flex flex-col gap-3 mt-6">
                <Button
                  asChild
                  variant="outline"
                  className="rounded-none bg-card border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.1em] text-xs h-11 justify-start"
                  data-testid="booking-phone-button"
                >
                  <a href={CONTACT.phoneHref}>
                    <Phone className="h-4 w-4 mr-2" /> {CONTACT.phone}
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-none bg-card border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.1em] text-xs h-11 justify-start"
                  data-testid="booking-email-button"
                >
                  <a href={CONTACT.emailHref}>
                    <Mail className="h-4 w-4 mr-2" /> {CONTACT.email}
                  </a>
                </Button>
              </div>
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mt-[120px]">
          <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.92] mb-10">Questions parents ask</h2>
          <Accordion type="single" collapsible className="border-t-[0.5px] border-foreground" data-testid="booking-faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b-[0.5px] border-foreground" data-testid="booking-faq-item">
                <AccordionTrigger className="text-left font-bold text-base sm:text-lg hover:no-underline py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </section>
    </>
  );
}
